import { useState } from 'react';
import { ChevronDown, ChevronRight, Trophy, Target, Hand, Shield } from 'lucide-react';
import { type LiveStandingsRow } from '../../lib/api/standings';
import { isMaintenanceTime } from '../../lib/maintenanceUtils';

interface LiveStandingsTableProps {
  standings: LiveStandingsRow[];
}

export function LiveStandingsTable({ standings }: LiveStandingsTableProps) {
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());

  const toggleUserExpansion = (userId: number) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  const getPositionIcon = (position: string) => {
    switch (position?.toUpperCase()) {
      case 'G':
        return <Shield size={16} className="text-info" />;
      case 'D':
      case 'LD':
      case 'RD':
        return <Shield size={16} className="text-warning" />;
      default:
        return <Target size={16} className="text-primary" />;
    }
  };

  const getPositionText = (position: string) => {
    switch (position?.toUpperCase()) {
      case 'G':
        return 'Gardien';
      case 'D':
      case 'LD':
      case 'RD':
        return 'Défenseur';
      default:
        return 'Attaquant';
    }
  };

  // Sort standings by total points (descending)
  const sortedStandings = [...standings].sort((a, b) => {
    const totalA = a.totgoals + a.totassists + a.totgoals_goalie + a.totassists_goalie + a.totwins_goalie + a.totshutout_goalie + a.tototlosses_goalie;
    const totalB = b.totgoals + b.totassists + b.totgoals_goalie + b.totassists_goalie + b.totwins_goalie + b.totshutout_goalie + b.tototlosses_goalie;
    return totalB - totalA;
  });

  if (standings.length === 0) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">Aucune donnée disponible</p>
      </div>
    );
  }

  return (
    <>
      {isMaintenanceTime() && (
        <div className="alert alert-info d-flex align-items-center mb-3" role="alert">
          <span className="me-2"></span>
          <div>
            <strong>Mode maintenance :</strong> Seules les statistiques en direct sont disponibles entre minuit et 5h AM (heure de Montréal).
          </div>
        </div>
      )}
      <div className="table-responsive">
      <table className="table table-hover">
        <thead className="table-primary">
          <tr>
            <th scope="col" className="text-center" style={{ width: '50px' }}>
              <Trophy size={20} />
            </th>
            <th scope="col">ÉQUIPE</th>
            <th scope="col" className="text-center">POINTS TOTAUX</th>
            <th scope="col" className="text-center">B</th>
            <th scope="col" className="text-center">A</th>
            <th scope="col" className="text-center">V-G</th>
            <th scope="col" className="text-center">BL</th>
            <th scope="col" className="text-center">DP-G</th>
            <th scope="col" className="text-center">B-G</th>
            <th scope="col" className="text-center">A-G</th>
            <th scope="col" className="text-center" style={{ width: '50px' }}></th>
          </tr>
        </thead>
        <tbody>
          {sortedStandings.map((standing, index) => {
            const totalPoints = standing.totgoals + standing.totassists + standing.totgoals_goalie + standing.totassists_goalie + standing.totwins_goalie + standing.totshutout_goalie + standing.tototlosses_goalie;
            const isExpanded = expandedUsers.has(standing.user_id);
            const hasPlayerStats = standing.playerStats && standing.playerStats.length > 0;

            return (
              <>
                <tr 
                  key={standing.user_id}
                  className={`cursor-pointer ${hasPlayerStats ? 'table-row-hover' : ''}`}
                  onClick={() => hasPlayerStats && toggleUserExpansion(standing.user_id)}
                  style={{ cursor: hasPlayerStats ? 'pointer' : 'default' }}
                >
                  <td className="text-center fw-bold">{index + 1}</td>
                  <td className="fw-bold">
                    <div className="d-flex align-items-center">
                      {hasPlayerStats && (
                        <span className="me-2 text-body">
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </span>
                      )}
                      <span className="text-body">{standing.username}</span>
                      {hasPlayerStats && (
                        <span className="badge bg-primary ms-2 small">
                          {standing.playerStats.length} joueur{standing.playerStats.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-center fw-bold text-primary">{totalPoints}</td>
                  <td className="text-center text-body">{standing.totgoals}</td>
                  <td className="text-center text-body">{standing.totassists}</td>
                  <td className="text-center text-body">{standing.totwins_goalie}</td>
                  <td className="text-center text-body">{standing.totshutout_goalie}</td>
                  <td className="text-center text-body">{standing.tototlosses_goalie}</td>
                  <td className="text-center text-body">{standing.totgoals_goalie}</td>
                  <td className="text-center text-body">{standing.totassists_goalie}</td>
                  <td className="text-center">
                    {hasPlayerStats && (
                      <span className="text-body-secondary small">
                        {isExpanded ? '▼' : '▶'}
                      </span>
                    )}
                  </td>
                </tr>
                
                {isExpanded && hasPlayerStats && (
                  <tr>
                    <td colSpan={11} className="p-0">
                      <div className="bg-body-tertiary border-top">
                        <div className="p-3">
                          <h6 className="mb-3 text-primary">
                            <Hand size={16} className="me-2" />
                            Détail des joueurs de {standing.username}
                          </h6>
                          <div className="table-responsive">
                            <table className="table table-sm table-striped mb-0">
                              <thead>
                                <tr className="table-secondary">
                                  <th style={{ width: '35%' }}>Joueur</th>
                                  <th className="text-center" style={{ width: '15%' }}>Position</th>
                                  <th className="text-center" style={{ width: '10%' }}>Buts</th>
                                  <th className="text-center" style={{ width: '10%' }}>Assists</th>
                                  <th className="text-center" style={{ width: '10%' }}>Victoires</th>
                                  <th className="text-center" style={{ width: '10%' }}>Blanch.</th>
                                  <th className="text-center" style={{ width: '10%' }}>DéfProl.</th>
                                  <th className="text-center fw-bold" style={{ width: '10%' }}>Points</th>
                                </tr>
                              </thead>
                              <tbody>
                                {standing.playerStats.map((player, playerIndex) => (
                                  <tr key={player.playerId} className={playerIndex % 2 === 0 ? 'table-dark' : ''}>
                                    <td className="fw-medium">
                                      <div className="d-flex align-items-center">
                                        <img 
                                          src={player.playerPhoto}
                                          alt={player.playerName}
                                          className="rounded-circle me-2 border"
                                          style={{ 
                                            width: '40px', 
                                            height: '40px', 
                                            objectFit: 'cover',
                                            backgroundColor: '#f8f9fa'
                                          }}
                                          onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            // Try NHL official headshot first
                                            if (!target.src.includes('assets.nhle.com')) {
                                              target.src = `https://assets.nhle.com/mugs/nhl/20232024/${player.playerId}.png`;
                                            } else {
                                              // Fallback to a placeholder
                                              target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiM2Yzc1N2QiLz4KPHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDEyQzE0LjIwOTEgMTIgMTYgMTAuMjA5MSAxNiA4QzE2IDUuNzkwODYgMTQuMjA5MSA0IDEyIDRDOS43OTA4NiA0IDggNS43OTA4NiA4IDhDOCAxMC4yMDkxIDkuNzkwODYgMTIgMTIgMTJaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTIgMTRDOC42ODYyOSAxNCA2IDE2LjY4NjMgNiAyMFYyMkg4VjIwQzggMTcuNzkwOSA5Ljc5MDg2IDE2IDEyIDE2QzE0LjIwOTEgMTYgMTYgMTcuNzkwOSAxNiAyMFYyMkgxOFYyMEMxOCAxNi42ODYzIDE1LjMxMzcgMTQgMTIgMTRaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4KPC9zdmc+';
                                            }
                                          }}
                                        />
                                        <div>
                                          <div className={`fw-bold ${playerIndex % 2 === 0 ? 'text-white' : 'text-body'}`}>
                                            {player.playerName}
                                          </div>
                                          <div className="d-flex align-items-center">
                                            {getPositionIcon(player.position)}
                                            <span className={`ms-1 small ${playerIndex % 2 === 0 ? 'text-white-50' : 'text-body-secondary'}`}>
                                              {getPositionText(player.position)}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="text-center">
                                      <span className="badge bg-secondary">
                                        {player.position}
                                      </span>
                                    </td>
                                    <td className="text-center">
                                      {player.goals > 0 && (
                                        <span className="badge bg-success">{player.goals}</span>
                                      )}
                                    </td>
                                    <td className="text-center">
                                      {player.assists > 0 && (
                                        <span className="badge bg-info">{player.assists}</span>
                                      )}
                                    </td>
                                    <td className="text-center">
                                      {player.wins > 0 && (
                                        <span className="badge bg-warning">{player.wins}</span>
                                      )}
                                    </td>
                                    <td className="text-center">
                                      {player.shutouts > 0 && (
                                        <span className="badge bg-dark">{player.shutouts}</span>
                                      )}
                                    </td>
                                    <td className="text-center">
                                      {player.otlosses > 0 && (
                                        <span className="badge bg-secondary">{player.otlosses}</span>
                                      )}
                                    </td>
                                    <td className="text-center fw-bold text-primary">
                                      {player.totalPoints}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
      </div>
      
      <div className="mt-3 p-3 bg-body-tertiary rounded">
        <p className="mb-2 small text-body-secondary">
          <strong>* À noter que les points sont déjà calculés dans la grille.</strong> Par exemple, une victoire de gardien affichera déjà 2 au lieu de 1
        </p>
        <p className="mb-0 small text-body-secondary">
          💡 <strong>Astuce :</strong> Cliquez sur une équipe qui a des joueurs actifs pour voir le détail de leurs performances en direct !
        </p>
      </div>
    </>
  );
}